import {
  CheckCircleFilled,
  PauseCircleFilled,
  PlayCircleFilled,
} from '@ant-design/icons';
import { ChevronRightIcon, XIcon } from '@heroicons/react/solid';
import { Avatar, Button, Modal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Pusher from 'pusher-js';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { shareModalVisibleState } from '@/atoms/modalAtom';
import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import GiftIcon from '../../../../../assets/Icons/GiftIcon';
import ShareIcon from '../../../../../assets/ShareIcons/ShareIcon';
import {
  authModalControl,
  tokenUpdate,
} from '../../../../atoms/authModalControl';
import { user } from '../../../../atoms/user';
import RecommendedBooksSection from '../../../../components/BookLandingPageComp/RecommendedBooksSection';
import { getUserInitials } from '../../../../components/ChatBox/Message';
import LiveChat from '../../../../components/Live/LiveChat';
import YoutubeLivePlayer from '../../../../components/Live/YoutubeLivePlayer';
import ShareButton from '../../../../components/PdfReader/ShareButton';
import { pageTitle } from '../../../../components/shared/basic/customHead';
import { ShouldRender } from '../../../../components/shared/basic/ShouldRender';
import { getLoginToken } from '../../../../components/shared/getToken';
import { notify } from '../../../../components/shared/notify';
import {
  EventDetails,
  logEvent,
} from '../../../../hooks/useClickEventTracking';
import {
  getCommentsBySessionId,
  getLiveSessionById,
  postComment,
} from '../../../../services/Live';
import { updateFollow } from '../../../../services/School';
import { keyFromYoutubeLiveLink } from '../../../../utils';
import type {
  GetCommentsData,
  IState,
  ReqCommentData,
} from '../../../../utils/interfaces/Live';
import { User } from '../../../../utils/interfaces/User';

const CHAT_WIDTH = 400;
const CHAT_WIDTH_SM = 300;

const LiveWatchPage: React.FC = () => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<IState>({
    isChatShow: true,
    page: 1,
    loading: true,
    token: '',
    comment: [],
    message: '',
    total: 0,
    per_page: 0,
    liveSession: {
      id: 0,
      title: '',
      iframe: '',
      work_shop: {
        author_name: '',
      },
      desc: '',
      category: '',
      author: {
        name: '',
        zl_uid: '',
        id: '',
        my_follower_count: 0,
        is_verified: false,
        is_followed: false,
        profile_data: {
          avatar: '',
          bio: '',
          experience_years: '',
          emp_work_experience: {
            company: '',
            designation: '',
          },
          user_name: '',
        },
      },
    },
  });

  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [followCount, setFollowCount] = useState(0);
  const [visible, setVisible] = useRecoilState(authModalControl);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [mainContentHeight, setMainContentHeight] = useState<number>(0);
  const [showControls, setShowControls] = useState(false);
  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
  const [isModalVisibleShare, setIsModalVisibleShare] = useRecoilState(
    shareModalVisibleState,
  );
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const { watchId } = router.query;
  const screens = useBreakpoint();

  const playerRef = useRef<any>(null);

  const handleScrollToTop = () => {
    if (typeof window !== 'undefined') {
      programmaticScrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };
  useEffect(() => {
    const resize = () => {
      if (mainContentRef.current) {
        setMainContentHeight(
          window.innerHeight - mainContentRef.current.offsetHeight - 130,
        );
      }
    };

    const observer = new ResizeObserver(resize);
    if (mainContentRef.current) {
      observer.observe(mainContentRef.current);
    }

    const waitForHeight = () => {
      if ((mainContentRef.current?.offsetHeight ?? 0) > 0) {
        resize();
      } else {
        requestAnimationFrame(waitForHeight);
      }
    };

    waitForHeight();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      observer.disconnect();
    };
  }, []);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight + 50;
    }
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleFollow = async ({
    author_id,
    is_follower,
  }: {
    author_id: string;
    is_follower: boolean;
  }) => {
    if (!state.token) {
      notify('Please login to Follow/Un-Follow', 'warning');
      setVisible((prevState) => ({
        ...prevState,
        login: true,
      }));
      return;
    }
    try {
      await updateFollow({
        author_id,
        is_follower,
      });
    } catch (error) {
      console.error('Error :', error);
    }
  };

  const handleFollowClick = ({
    followState,
    author_id,
    author_name,
  }: {
    followState: string;
    author_id: string;
    author_name: string;
  }) => {
    const eventParams: EventDetails = {
      event_name: followState,
      source: 'global',
      source_type: 'author',
      source_id: author_id as string,
      source_name: author_name,
      sub_source: 'live',
      sub_source_id: watchId as string,
      sub_source_name: state?.liveSession?.title,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'live_watching_screen',
      },
    };
    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };
  const toggleFollow = () => {
    const newState = !isFollowing;

    handleFollowClick({
      followState: newState ? 'follow' : 'unfollow',
      author_id: state?.liveSession?.author?.id,
      author_name: state?.liveSession?.author?.name as string,
    });

    setIsFollowing(newState);

    setFollowCount((count) => (newState ? count + 1 : count - 1));
    handleFollow({
      author_id: state?.liveSession?.author?.id?.toString() as string,
      is_follower: newState,
    });
  };

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setState((prevState) => ({
        ...prevState,
        token: token as string,
      }));
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    const updateToken = async () => {
      await fetchData();
    };
    updateToken();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged, visible.login, visible.signUp]);

  useEffect(() => {
    if (state.token) {
      getCommentHandle(+(watchId as string));
    }
  }, [state?.token]);

  const sendMessageHandle = async () => {
    setState((prevState) => ({
      ...prevState,
      message: '',
    }));

    const payload: ReqCommentData = {
      commentable_type: 'zl_live',
      commentable_id: watchId as string,
      comment: state.message,
    };

    try {
      await postComment(payload);
      scrollToBottom();
    } catch (e) {
      console.error(e);
    }
  };

  const handleMessageSummit = async () => {
    setState((prevState) => ({
      ...prevState,
      message: '',
    }));

    const payload: ReqCommentData = {
      commentable_type: 'zl_live',
      commentable_id: watchId as string,
      comment: state.message,
    };

    try {
      await postComment(payload);
      scrollToBottom();
    } catch (e) {
      console.error(e);
    }
  };

  const getLiveSessionHandle = async () => {
    try {
      setState((prevState) => ({
        ...prevState,
        loading: true,
      }));

      const response = await getLiveSessionById(watchId as string);
      setState((prevState) => ({
        ...prevState,
        liveSession: { ...response.data, loading: false },
      }));

      setFollowCount(response?.data?.author?.my_follower_count || 0);
      await getCommentHandle(response?.data?.id);
      handleScrollToTop();
    } catch (e) {
      console.error(e);
    } finally {
      setState((prevState) => ({
        ...prevState,
        loading: false,
      }));
    }
  };
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.innerWidth < 768 &&
      isChatOpen
    ) {
      handleScrollToTop();

      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isChatOpen]);

  const getCommentHandle = async (id: number) => {
    try {
      if (isNaN(id)) return;

      const response = await getCommentsBySessionId(id);
      const rawData = response?.data || [];

      const formattedComments: GetCommentsData[] = rawData.map((item: any) => ({
        id: item.id,
        text: item.comment,
        user: {
          name: item?.user?.name || 'Anonymous',
          avatar: item?.user?.avatar || '',
        },
      }));

      setState((prevState) => ({
        ...prevState,
        comment: formattedComments,
        page: response?.data?.meta?.current_page,
        per_page: response?.data?.meta?.per_page,
      }));
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if (watchId) {
      getLiveSessionHandle();
    }
  }, [watchId]);

  useEffect(() => {
    scrollToBottom();
  }, [state?.comment, state.liveSession]);

  const variants = {
    initial: !screens.md ? { y: '100%' } : { x: '100%' },
    animate: { x: 0, y: 0 },
    exit: !screens.md ? { y: '100%' } : { x: '100%' },
  };
  useEffect(() => {
    if (watchId) {
      const pusher = new Pusher(process.env.NEXT_PUBLIC_PUSHER_KEY as string, {
        cluster: process.env.NEXT_PUBLIC_CLUSTER as string,
      });

      const channelName = process.env.NEXT_PUBLIC_CHANNEL_NAME as string;
      const eventName = `${process.env.NEXT_PUBLIC_EVENT_NAME}-${watchId}`;

      const channel = pusher.subscribe(channelName);

      const handleChatEvent = (data: any) => {
        const formattedData = {
          id: data.id,
          text: data.comment,
          user: {
            name: data?.user?.name || 'Anonymous',
            avatar: data?.user?.avatar || '',
          },
        };
        setState((prevState) => ({
          ...prevState,
          comment: [...prevState.comment, formattedData],
        }));
      };

      channel.bind(eventName, handleChatEvent);

      return () => {
        channel.unbind(eventName, handleChatEvent);
        pusher.unsubscribe(channelName);
        pusher.disconnect();
      };
    }

    return undefined;
  }, [watchId]);

  const getMainContentWidth = () => {
    if (!screens.md || !isChatOpen) {
      return '100%';
    }
    return `calc(100% - ${screens.lg ? CHAT_WIDTH : CHAT_WIDTH_SM}px)`;
  };

  const mainContentVariants = {
    expanded: {
      width: '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
    collapsed: {
      width: screens.md
        ? `calc(100% - ${screens.lg ? CHAT_WIDTH : CHAT_WIDTH_SM}px)`
        : '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
  };

  const toggleFullScreen = () => {
    if (!state?.token)
      return notify('Please login to enable fullscreen', 'warning');
    const layout = document.querySelector('.iframe-fullscreen') as any;

    if (layout) {
      if (!isFullScreen) {
        const fullscreenFunction =
          layout.requestFullscreen ||
          layout.webkitRequestFullscreen ||
          layout.msRequestFullscreen ||
          layout.mozRequestFullScreen;

        if (fullscreenFunction) {
          fullscreenFunction.call(layout);
        }
      } else {
        const docs = document as any;
        const exitFullscreenFunction =
          docs.exitFullscreen ||
          docs.webkitExitFullscreen ||
          docs.msExitFullscreen ||
          docs.mozCancelFullScreen;

        if (exitFullscreenFunction) {
          exitFullscreenFunction.call(docs);
        }
      }
    }

    setIsFullScreen(!isFullScreen);
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        !!document.fullscreenElement ||
          !!(document as any).webkitFullscreenElement ||
          !!(document as any).msFullscreenElement ||
          !!(document as any).mozFullScreenElement,
      );
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullScreenChange,
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullScreenChange,
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullScreenChange,
      );
    };
  }, []);

  const fullScreenIcon = (
    <div className='group'>
      <button
        onClick={toggleFullScreen}
        className={clsx(
          isFullScreen ? ' text-primary' : 'text-white ',
          'flex items-center  justify-center relative cursor-pointer group-hover:bg-gray-600 group-hover:backdrop-blur-xl group-hover:bg-opacity-50  p-2 w-8 h-8 md:w-10 md:h-10 rounded-full',
        )}
      >
        <div className='w-4 h-4 md:w-5 md:h-5 '>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='none'
            className='fill-current'
          >
            <path d='M23 4C23 2.34315 21.6569 1 20 1H16C15.4477 1 15 1.44772 15 2C15 2.55228 15.4477 3 16 3H20C20.5523 3 21 3.44772 21 4V8C21 8.55228 21.4477 9 22 9C22.5523 9 23 8.55228 23 8V4Z' />
            <path d='M23 16C23 15.4477 22.5523 15 22 15C21.4477 15 21 15.4477 21 16V20C21 20.5523 20.5523 21 20 21H16C15.4477 21 15 21.4477 15 22C15 22.5523 15.4477 23 16 23H20C21.6569 23 23 21.6569 23 20V16Z' />
            <path d='M4 21H8C8.55228 21 9 21.4477 9 22C9 22.5523 8.55228 23 8 23H4C2.34315 23 1 21.6569 1 20V16C1 15.4477 1.44772 15 2 15C2.55228 15 3 15.4477 3 16V20C3 20.5523 3.44772 21 4 21Z' />
            <path d='M1 8C1 8.55228 1.44772 9 2 9C2.55228 9 3 8.55228 3 8L3 4C3 3.44772 3.44772 3 4 3H8C8.55228 3 9 2.55228 9 2C9 1.44772 8.55228 1 8 1H4C2.34315 1 1 2.34315 1 4V8Z' />
          </svg>
        </div>
      </button>
    </div>
  );

  const handlePlay = () => {
    if (
      playerRef.current &&
      typeof playerRef.current.playVideo === 'function'
    ) {
      playerRef.current.playVideo();
    }
  };

  const handlePause = () => {
    if (
      playerRef.current &&
      typeof playerRef.current.pauseVideo === 'function'
    ) {
      playerRef.current.pauseVideo();
    }
  };

  const handleMouseActivity = () => {
    setShowControls(true);

    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

    inactivityTimer.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, []);

  return (
    <>
      <Head>
        <title>{pageTitle('Watch')}</title>
      </Head>

      <div className='min-h-screen text-white mt-14' ref={containerRef}>
        <motion.div
          className='relative'
          variants={mainContentVariants}
          animate={!screens.md || !isChatOpen ? 'expanded' : 'collapsed'}
          style={{
            width: getMainContentWidth(),
            float: 'left',
          }}
        >
          <div className='relative'>
            <ShouldRender check={state?.liveSession?.iframe && !state?.loading}>
              <div
                ref={mainContentRef}
                onMouseMove={handleMouseActivity}
                className='iframe-fullscreen disableYoutubeUI no-select relative group'
              >
                <YoutubeLivePlayer
                  isRounded={state?.isChatShow}
                  token={state?.token}
                  isFullscreen={isFullScreen}
                  playerRef={playerRef}
                  videoId={keyFromYoutubeLiveLink(state?.liveSession.iframe)}
                  setIsPlaying={setIsPlaying}
                  isPlaying={isPlaying}
                />

                <span className='absolute z-40 bottom-[2%] right-[1%]'>
                  {fullScreenIcon}
                </span>

                <ShouldRender check={!isPlaying && showControls}>
                  <span
                    onClick={() => handlePlay()}
                    className='absolute z-40 cursor-pointer bottom-1/2 right-1/2 translate-x-1/2 translate-y-1/2'
                  >
                    <PlayCircleFilled className='text-4xl md:text-5xl hover:text-gray-200 bg-bgColor rounded-full hover:scale-[1.1]' />
                  </span>
                </ShouldRender>

                <ShouldRender check={isPlaying && showControls}>
                  <span
                    onClick={() => handlePause()}
                    className='absolute z-40 cursor-pointer bottom-1/2 right-1/2 translate-x-1/2 translate-y-1/2'
                  >
                    <PauseCircleFilled className='text-4xl md:text-5xl hover:text-gray-200 bg-bgColor rounded-full hover:scale-[1.1]' />
                  </span>
                </ShouldRender>
              </div>

              <div className='relative'>
                <div className='w-full bg-primary h-[2px]'></div>
                <div className='h-3 w-3 bg-white rounded-full absolute right-0 -top-[5px]' />
              </div>
            </ShouldRender>

            <div className=''>
              <div className='flex items-center justify-between mb-4 py-4 px-4 lg:px-6 bg-[#151515] gap-x-2'>
                <div className='flex items-center space-x-2 lg:space-x-3'>
                  <div className='bg-bgColor lg:p-3 rounded-full w-9 h-9 lg:w-12 lg:h-12 flex justify-center items-center border border-[#282829]'>
                    {' '}
                    <GiftIcon />
                  </div>

                  <div>
                    <h3 className='font-semibold text-white text-xs lg:text-base'>
                      Watch with a Friend & Grow Together
                    </h3>
                    <p className='font-[400] text-textGrayColor text-[10px] lg:text-sm '>
                      Also you will get a surprise when your friend joins.
                    </p>
                  </div>
                </div>

                <ShouldRender check={!screens.lg}>
                  <div
                    onClick={(e) => {
                      e.preventDefault();

                      if (!state.token) {
                        setVisible((prevState) => ({
                          ...prevState,
                          login: true,
                        }));
                        notify('Please login to invite a friend', 'warning');
                        setIsModalVisibleShare(false);
                      } else {
                        setIsModalVisibleShare(!isModalVisibleShare);
                      }
                    }}
                    className='bg-[#282829] text-white  font-semibold  rounded-full w-6 h-6 flex justify-center items-center  text-sm gap-1'
                  >
                    <ChevronRightIcon className='w-5 h-5s' />
                  </div>
                </ShouldRender>
                <ShouldRender check={screens.lg}>
                  <ShareButton
                    isReadingScreen={false}
                    isLoggedIN={!!state?.token}
                    hideIcon={screens.lg}
                    type='invite'
                  />
                </ShouldRender>
              </div>

              <div className='mt-6 px-4'>
                <h2 className='text-base md:text-lg lg:text-xl font-bold mb-6'>
                  {state?.liveSession?.title}
                </h2>

                <div className='flex items-center flex-col lg:flex-row w-full gap-y-3 justify-between'>
                  <div className='flex items-center  rounded-t-xl w-full  justify-between lg:justify-start'>
                    <div className='flex items-center gap-3.5 pr-6'>
                      <div className='w-[35px] lg:w-[48px]'>
                        <Avatar
                          size={screens.lg ? 48 : 35}
                          alt='profile-picture'
                          style={{ fontSize: '16px' }}
                          src={`${process.env.NEXT_PUBLIC_STUDIO_PATH}/${state?.liveSession?.author?.profile_data?.avatar}`}
                        >
                          {state?.liveSession?.author?.name &&
                            getUserInitials(state?.liveSession?.author?.name)}
                        </Avatar>
                      </div>

                      <div className='flex items-center gap-2'>
                        <p
                          onClick={() =>
                            state?.liveSession?.author?.profile_data
                              ?.user_name &&
                            router.push(
                              `/author/${state?.liveSession?.author?.profile_data?.user_name}`,
                            )
                          }
                          className='text-sm md:text-base font-semibold hover:text-white cursor-pointer hover:underline'
                        >
                          {state?.liveSession?.author?.name}
                        </p>
                        {state?.liveSession?.author.is_verified && (
                          <span className='text-red-500'>
                            <CheckCircleFilled className='h-4 w-4 fill-current' />
                          </span>
                        )}
                      </div>
                      <ShouldRender check={followCount >= 1000}>
                        <p className='font-[400] text-textGrayColor text-sm'>
                          {followCount} Learners
                        </p>
                      </ShouldRender>
                    </div>
                    {/* <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, ease: 'easeInOut' }}
                    >
                      <Button
                        className='border-[#282829] bg-transparent text-white border rounded-full px-5 py-2'
                        onClick={() => {
                          isFollowing
                            ? setIsModalVisible(true)
                            : toggleFollow();
                        }}
                      >
                        <motion.span
                          key={
                            state?.liveSession?.author?.is_followed
                              ? 'unfollow'
                              : 'follow'
                          }
                          initial={{
                            y: state?.liveSession?.author?.is_followed
                              ? -10
                              : 10,
                            opacity: 0,
                          }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{
                            y: state?.liveSession?.author?.is_followed
                              ? 10
                              : -10,
                            opacity: 0,
                          }}
                          transition={{ duration: 0.3 }}
                          className='text-white'
                        >
                          {state?.liveSession?.author?.is_followed
                            ? 'Following'
                            : 'Follow'}
                        </motion.span>
                      </Button>
                    </motion.div> */}

                    <ShouldRender check={!screens.lg}>
                      <Button
                        onClick={(e) => {
                          e.preventDefault();

                          if (!state.token) {
                            setVisible((prevState) => ({
                              ...prevState,
                              login: true,
                            }));
                            notify('Please login to share', 'warning');
                            setIsModalVisibleShare(false);
                          } else {
                            setIsModalVisibleShare(!isModalVisibleShare);
                          }
                        }}
                        icon={<ShareIcon width='18' />}
                        className='border-none rounded-full p-0 px-3 py-4 h-[33px] md:h-[38px] bg-[#282829] text-sm font-semibold text-white'
                      >
                        Share
                      </Button>
                    </ShouldRender>
                  </div>

                  <div className='flex w-full lg:w-fit items-center gap-4'>
                    {/* <div className='flex items-center justify-center'>
                      <motion.div
                        className='flex items-center bg-[#282829] rounded-full px-3 py-1 md:py-1.5 gap-2'
                        initial={{ scale: 1 }}
                        onHoverStart={() => setIsHovered(true)}
                        onHoverEnd={() => setIsHovered(false)}
                      >
                        <motion.button
                          className='flex items-center gap-1 text-white'
                          onClick={handleLike}
                          whileTap={{ scale: 0.9 }}
                        >
                          <motion.span
                            animate={{
                              color: hasLiked ? 'white' : 'white',
                              scale: hasLiked ? [1, 1.2, 1] : 1,
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            {hasLiked ? (
                              <LikeFilled style={{ fontSize: '16px' }} />
                            ) : (
                              <LikeOutlined style={{ fontSize: '16px' }} />
                            )}
                          </motion.span>

                          <AnimatePresence mode='wait'>
                            <motion.span
                              key={likes}
                              className='text-sm font-medium min-w-[24px]'
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.2 }}
                            >
                              {likes.toString().padStart(2, '0')}
                            </motion.span>
                          </AnimatePresence>
                        </motion.button>

                        <motion.div
                          className='h-4 w-px bg-gray-700'
                          animate={{
                            height: isHovered ? '16px' : '12px',
                            opacity: isHovered ? 0.8 : 0.5,
                          }}
                        />

                        <motion.button
                          className='text-white'
                          onClick={handleDislike}
                          whileTap={{ scale: 0.9 }}
                        >
                          <motion.span
                            animate={{
                              color: hasDisliked ? '#ff4d4f' : 'white',
                              scale: hasDisliked ? [1, 1.2, 1] : 1,
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            {hasDisliked ? (
                              <DislikeFilled style={{ fontSize: '16px' }} />
                            ) : (
                              <DislikeOutlined style={{ fontSize: '16px' }} />
                            )}
                          </motion.span>
                        </motion.button>
                      </motion.div>
                    </div> */}
                    <ShareButton
                      isReadingScreen={false}
                      isLoggedIN={!!state?.token}
                      type={screens.lg ? 'default' : 'no-icon'}
                    />

                    <ShouldRender check={screens.md && !isChatOpen}>
                      <Button
                        onClick={toggleChat}
                        className='text-gray-300 hover:text-white'
                      >
                        {isChatOpen ? 'Close Chat' : 'Open Chat'}
                      </Button>
                    </ShouldRender>
                  </div>
                  <ShouldRender check={!screens.md}>
                    <div
                      onClick={toggleChat}
                      className='bg-[#151515] w-full rounded-[12px] px-3.5 py-2.5  relative'
                    >
                      <div className='flex items-center gap-x-2 mb-2'>
                        <span className='animate-pulse bg-[#C72707] w-2 h-2 rounded-full'></span>
                        <span className='text-white text-sm font-medium'>
                          Live chat
                        </span>
                      </div>

                      <div className='relative flex gax-x-3 w-full  space-x-3 pb-1 items-center'>
                        <div className='w-[24px] '>
                          <Avatar
                            size={24}
                            alt='profile-picture'
                            style={{ fontSize: '16px' }}
                            src={`${process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM}/${userData?.avatar}`}
                          >
                            {state?.liveSession?.author?.name &&
                              getUserInitials(state?.liveSession?.author?.name)}
                          </Avatar>
                        </div>
                        <div className='bg-[#282829]  flex justify-start pl-3 items-center h-[30px]  text-xs rounded-full text-[#C7C6CF] w-full'>
                          Chat as {userData?.name}..
                        </div>
                      </div>
                    </div>
                  </ShouldRender>
                </div>
                <div className='mt-10'>
                  <RecommendedBooksSection
                    category={state?.liveSession?.category ?? undefined}
                    token={state?.token}
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {isChatOpen && (
            <motion.div
              key={`chat-${isChatOpen}`}
              initial={variants.initial}
              animate={variants.animate}
              exit={variants.exit}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{
                width: screens.md
                  ? `${screens.lg ? CHAT_WIDTH : CHAT_WIDTH_SM}px`
                  : '100%',
                height: !screens.md ? `${mainContentHeight}px ` : '94%',
              }}
              className={clsx(
                'fixed bottom-0 right-0 md:top-14 bg-[#1a1a1a] z-40 shadow-xl rounded-t-2xl overflow-hidden',
              )}
            >
              <div className='flex justify-between items-center px-4 py-2 border-b border-gray-700'>
                <h3 className='text-white font-medium'>Live Chat</h3>
                <button onClick={() => setIsChatOpen(false)}>
                  <XIcon className='h-5 w-5 text-white' />
                </button>
              </div>
              <div className='h-full'>
                <LiveChat
                  toggleChat={toggleChat}
                  comments={state.comment}
                  setState={setState}
                  handleMessageSummit={handleMessageSummit}
                  sendMessageHandle={sendMessageHandle}
                  message={state?.message}
                  isSignedIn={!!userData}
                  userName={userData?.name}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className='clear-both'></div>

        <Modal
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width='400px'
          className='bg-[#212121] rounded-lg'
          closable={false}
          centered={true}
          destroyOnHidden
        >
          <div className='flex flex-col'>
            <div className='py-5'>Sure want to Un-Follow ?</div>
            <div className='flex gap-x-1 justify-end'>
              <span>
                <Button onClick={handleCancel} type='text'>
                  No
                </Button>
              </span>
              <span>
                <Button
                  onClick={() => {
                    toggleFollow();
                    setIsModalVisible(false);
                  }}
                  type='text'
                  className='text-blue-500 font-medium'
                >
                  Yes
                </Button>
              </span>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default LiveWatchPage;
